import { apiSlice } from './apiSlice';
import type {
  InventoryLot,
  InventoryTransaction,
  InventoryReservation,
  WithdrawPayload,
  ReservePayload,
  CorrectPayload,
  ReleaseReservationPayload,
} from '../types/inventory';

// Mutations invalidate the lot list (balances change), the lot's transaction
// timeline, and the open-reservations list.
const invalidateLot = (saveId: string, lotId?: string) => [
  { type: 'InventoryLot' as const, id: 'LIST' },
  ...(lotId ? [{ type: 'InventoryTransaction' as const, id: lotId }] : []),
  { type: 'InventoryTransaction' as const, id: 'LIST' },
  { type: 'InventoryTransaction' as const, id: 'RESERVATIONS' },
];

export const inventoryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryLots: builder.query<InventoryLot[], { saveId: string }>({
      query: ({ saveId }) => ({ url: `/saves/${saveId}/inventory/lots` }),
      transformResponse: (resp: { lots: InventoryLot[] }) => resp.lots ?? [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((lot) => ({ type: 'InventoryLot' as const, id: lot.id })),
              { type: 'InventoryLot' as const, id: 'LIST' },
            ]
          : [{ type: 'InventoryLot' as const, id: 'LIST' }],
    }),

    getInventoryReservations: builder.query<InventoryReservation[], { saveId: string; lotId?: string; status?: string }>({
      query: ({ saveId, lotId, status }) => ({
        url: `/saves/${saveId}/inventory/reservations`,
        params: { ...(lotId ? { lotId } : {}), ...(status !== undefined ? { status } : {}) },
      }),
      transformResponse: (resp: { reservations: InventoryReservation[] }) => resp.reservations ?? [],
      providesTags: [{ type: 'InventoryTransaction', id: 'RESERVATIONS' }],
    }),

    getInventoryTransactions: builder.query<InventoryTransaction[], { saveId: string; lotId?: string }>({
      query: ({ saveId, lotId }) => ({
        url: `/saves/${saveId}/inventory/transactions`,
        params: lotId ? { lotId } : undefined,
      }),
      transformResponse: (resp: { transactions: InventoryTransaction[] }) => resp.transactions ?? [],
      providesTags: (_r, _e, { lotId }) => [
        { type: 'InventoryTransaction' as const, id: lotId || 'LIST' },
        { type: 'InventoryTransaction' as const, id: 'LIST' },
      ],
    }),

    withdrawInventory: builder.mutation<any, { saveId: string; lotId: string; payload: WithdrawPayload }>({
      query: ({ saveId, lotId, payload }) => ({
        url: `/saves/${saveId}/inventory/${lotId}/withdraw`, method: 'POST', body: payload,
      }),
      invalidatesTags: (_r, _e, { lotId }) => invalidateLot('', lotId),
    }),

    reserveInventory: builder.mutation<any, { saveId: string; lotId: string; payload: ReservePayload }>({
      query: ({ saveId, lotId, payload }) => ({
        url: `/saves/${saveId}/inventory/${lotId}/reserve`, method: 'POST', body: payload,
      }),
      invalidatesTags: (_r, _e, { lotId }) => invalidateLot('', lotId),
    }),

    correctInventory: builder.mutation<any, { saveId: string; lotId: string; payload: CorrectPayload }>({
      query: ({ saveId, lotId, payload }) => ({
        url: `/saves/${saveId}/inventory/${lotId}/correct`, method: 'POST', body: payload,
      }),
      invalidatesTags: (_r, _e, { lotId }) => invalidateLot('', lotId),
    }),

    releaseReservation: builder.mutation<any, { saveId: string; reservationId: string; lotId?: string; payload: ReleaseReservationPayload }>({
      query: ({ saveId, reservationId, payload }) => ({
        url: `/saves/${saveId}/inventory/reservations/${reservationId}/release`, method: 'POST', body: payload,
      }),
      invalidatesTags: (_r, _e, { lotId }) => invalidateLot('', lotId),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetInventoryLotsQuery,
  useGetInventoryReservationsQuery,
  useGetInventoryTransactionsQuery,
  useWithdrawInventoryMutation,
  useReserveInventoryMutation,
  useCorrectInventoryMutation,
  useReleaseReservationMutation,
} = inventoryApi;

export default inventoryApi;
